import { useMutation } from '@apollo/client';
import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Loader } from 'components/Loader/Loader';
import { CONFIRM_APPOINTMENT } from 'kb-shared/graphql/mutations';
import { ConfirmAppointmentData } from 'kb-shared/graphql/types';
import { BugTracker } from 'kb-shared/utilities/bugTracker';
import { getUserFriendlyError } from 'utilities/errors';
import { showSuccessToast, showErrorToast } from 'utilities/notificationUtils';

type ConfirmAppointmentVariables = {
  appointmentId: number;
  startTimestamp?: number;
  endTimestamp?: number;
};

const SUCCESS_MSG = 'Your appointment has been confirmed!';
const ERROR_MSG = 'There was an issue confirming your appointment. Please try again.';

export const ConfirmAppointment: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const parsed = useMemo<ConfirmAppointmentVariables | null>(() => {
    const q = new URLSearchParams(location.search).get('lookupKey');
    if (!q) return null;

    const parts = q.split('-');
    const appointmentId = parseInt(parts[0], 10);
    if (Number.isNaN(appointmentId)) return null;

    if (parts.length === 3) {
      const startTimestamp = parseInt(parts[1], 10);
      const endTimestamp = parseInt(parts[2], 10);
      if (!Number.isNaN(startTimestamp) && !Number.isNaN(endTimestamp)) {
        return { appointmentId, startTimestamp, endTimestamp };
      }
    }

    return { appointmentId };
  }, [location.search]);

  const [confirmAppointment, {}] = useMutation<ConfirmAppointmentData, ConfirmAppointmentVariables>(
    CONFIRM_APPOINTMENT,
    {
      onCompleted: data => {
        const id = data?.confirmAppointment?.appointment?.id;
        const ok = Boolean(id);

        if (!ok) {
          BugTracker.notify('No data received from BE', 'ConfirmAppointment');
          showErrorToast(ERROR_MSG);
        } else {
          showSuccessToast(SUCCESS_MSG);
        }

        const params = new URLSearchParams({
          toast_type: ok ? 'success' : 'error',
          toast_message: ok ? SUCCESS_MSG : ERROR_MSG
        });

        navigate(`/?${params.toString()}`, { replace: true });
      },
      onError: error => {
        const userFriendlyError = getUserFriendlyError(error);
        if (!userFriendlyError) {
          BugTracker.notify(error, 'ConfirmAppointment');
        }

        const message = userFriendlyError || ERROR_MSG;
        showErrorToast(message);

        const params = new URLSearchParams({
          toast_type: 'error',
          toast_message: message
        });

        navigate(`/?${params.toString()}`, { replace: true });
      }
    }
  );

  useEffect(() => {
    if (parsed != null) {
      confirmAppointment({ variables: parsed });
    }
  }, [parsed, confirmAppointment]);

  return <Loader absoluteCentered />;
};
